/* =============================================================================
   ALLUREIS — SNAPMAGS SHOPIFY THEME JS
   Vanilla JS only. No frameworks. Mobile-first. iPhone-native feel.
   Modules (IIFEs) in order:
     1. Utils
     2. Toast
     3. Header + Mobile Nav
     4. Cart (Shopify AJAX API)
     5. Cart Drawer
     6. Email Popup
     7. Before/After slider
     8. FAQ Accordion
     9. Product Page (variant logic, bundle, sticky ATC, urgency)
    10. DOMReady init
   ============================================================================= */

'use strict';

const $ = (sel, ctx = document) => ctx.querySelector(sel);
const $$ = (sel, ctx = document) => [...ctx.querySelectorAll(sel)];

// Shared scroll-lock helpers — every drawer/popup (cart drawer, email popup,
// bundle upsell popup, mobile nav) locks body scroll the same way. Setting
// overflow:hidden on body removes the scrollbar, which shrinks the viewport
// width and shifts all fixed/centered content left by the scrollbar's width
// (~15-17px desktop). Compensating with padding-right holds everything in
// place. Reference-counted so nested opens (e.g. popup opened while nav is
// open) don't unlock prematurely.
let __scrollLockCount = 0;
function lockBodyScroll() {
  if (__scrollLockCount === 0) {
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = 'hidden';
    if (scrollbarWidth > 0) document.body.style.paddingRight = `${scrollbarWidth}px`;
  }
  __scrollLockCount++;
}
function unlockBodyScroll() {
  __scrollLockCount = Math.max(0, __scrollLockCount - 1);
  if (__scrollLockCount === 0) {
    document.body.style.overflow = '';
    document.body.style.paddingRight = '';
  }
}

function formatMoney(cents, currency = 'USD') {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency, minimumFractionDigits: 2 }).format(cents / 100);
}

const Toast = (() => {
  let container;
  function getContainer() {
    if (!container) {
      container = document.getElementById('toast-container');
      if (!container) { container = document.createElement('div'); container.id = 'toast-container'; document.body.appendChild(container); }
    }
    return container;
  }
  function show(message, duration = 3000) {
    const c = getContainer();
    const el = document.createElement('div');
    el.className = 'toast';
    el.textContent = message;
    c.appendChild(el);
    setTimeout(() => { el.style.opacity = '0'; el.style.transition = 'opacity 0.3s'; setTimeout(() => el.remove(), 300); }, duration);
  }
  return { show };
})();

const Header = (() => {
  let header, hamburger, mobileNav, lastScrollY, ticking;
  function init() {
    header = document.getElementById('site-header');
    hamburger = document.getElementById('hamburger-btn');
    mobileNav = document.getElementById('mobile-nav');
    if (!header) return;
    lastScrollY = window.scrollY;
    const isHome = document.body.classList.contains('is-home');
    if (!isHome) header.classList.add('is-visible');
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    if (hamburger) hamburger.addEventListener('click', toggleMobileNav);
    if (mobileNav) {
      $$('a', mobileNav).forEach(a => a.addEventListener('click', closeMobileNav));
      const closeBtn = document.getElementById('mobile-nav-close');
      if (closeBtn) closeBtn.addEventListener('click', closeMobileNav);
    }
  }
  function onScroll() { if (ticking) return; ticking = true; requestAnimationFrame(update); }
  function update() {
    ticking = false;
    const y = window.scrollY;
    const isHome = document.body.classList.contains('is-home');
    if (isHome) {
      if (y > 1) { header.classList.add('is-visible'); header.classList.remove('is-hidden'); }
      else header.classList.remove('is-visible');
    } else {
      header.classList.add('is-visible');
      if (y > lastScrollY && y > 120) header.classList.add('is-hidden');
      else header.classList.remove('is-hidden');
    }
    lastScrollY = y;
  }
  function toggleMobileNav() { const isOpen = mobileNav && mobileNav.classList.contains('is-open'); if (isOpen) closeMobileNav(); else openMobileNav(); }
  function openMobileNav() { if (!mobileNav) return; mobileNav.classList.add('is-open'); if (hamburger) hamburger.classList.add('is-open'); lockBodyScroll(); }
  function closeMobileNav() { if (!mobileNav) return; mobileNav.classList.remove('is-open'); if (hamburger) hamburger.classList.remove('is-open'); unlockBodyScroll(); }
  return { init };
})();

const Cart = (() => {
  let _state = { items: [], item_count: 0, total_price: 0, currency: 'USD' };
  const listeners = [];
  function subscribe(fn) { listeners.push(fn); }
  function notify() { listeners.forEach(fn => fn(_state)); }
  async function fetch() {
    try { const r = await window.fetch('/cart.js'); _state = await r.json(); notify(); return _state; }
    catch (e) { console.error('[Cart] fetch failed', e); return _state; }
  }
  async function addItem({ variantId, quantity = 1, properties = {} }) {
    const body = JSON.stringify({ id: variantId, quantity, properties });
    const r = await window.fetch('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body });
    if (!r.ok) { const err = await r.json().catch(() => ({})); throw new Error(err.description || 'Could not add item'); }
    await fetch();
    return _state;
  }
  async function addItems(items) { for (const item of items) { await addItem(item); } return _state; }
  async function changeItem({ key, quantity }) {
    const r = await window.fetch('/cart/change.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ id: key, quantity }) });
    if (!r.ok) throw new Error('Cart change failed');
    _state = await r.json(); notify(); return _state;
  }
  async function removeItem(key) { return changeItem({ key, quantity: 0 }); }
  function getState() { return _state; }
  async function goToCheckout() { window.open('/checkout', '_self'); }
  return { fetch, addItem, addItems, changeItem, removeItem, getState, subscribe, goToCheckout };
})();

const CartDrawer = (() => {
  let drawer, overlay;
  function init() {
    drawer = document.getElementById('cart-drawer');
    overlay = document.getElementById('cart-overlay');
    if (!drawer) return;
    $$('[data-cart-open]').forEach(el => el.addEventListener('click', open));
    if (overlay) overlay.addEventListener('click', close);
    const closeBtn = drawer.querySelector('[data-cart-close]');
    if (closeBtn) closeBtn.addEventListener('click', close);
    const checkoutBtn = drawer.querySelector('[data-cart-checkout]');
    if (checkoutBtn) checkoutBtn.addEventListener('click', () => Cart.goToCheckout());
    const continueBtn = drawer.querySelector('[data-cart-continue]');
    if (continueBtn) continueBtn.addEventListener('click', close);
    document.addEventListener('keydown', e => { if (e.key === 'Escape' && drawer.classList.contains('is-open')) close(); });
    Cart.subscribe(renderCart);
    // Prefetch the "Complete your look" catalog in the background instead of
    // waiting for the first cart-drawer open to kick off the request — that
    // network round trip is what made the upsell strip feel laggy on first
    // open, especially on mobile connections.
    if ('requestIdleCallback' in window) requestIdleCallback(getUpsellCatalog);
    else setTimeout(getUpsellCatalog, 1000);
  }
  function open() { drawer.classList.add('is-open'); if (overlay) overlay.classList.add('is-open'); lockBodyScroll(); Cart.fetch(); }
  function close() { drawer.classList.remove('is-open'); if (overlay) overlay.classList.remove('is-open'); unlockBodyScroll(); }
  function renderCart(state) {
    $$('.cart-count').forEach(el => { el.textContent = state.item_count; el.classList.toggle('has-items', state.item_count > 0); });
    const body = drawer.querySelector('.cart-body');
    const footer = drawer.querySelector('.cart-footer');
    if (!body) return;
    renderUpsells(state);
    if (state.item_count === 0) {
      body.innerHTML = `<div class="cart-empty"><svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M15.75 10.5V6a3.75 3.75 0 10-7.5 0v4.5m11.356-1.993l1.263 12c.07.665-.45 1.243-1.119 1.243H4.25a1.125 1.125 0 01-1.12-1.243l1.264-12A1.125 1.125 0 015.513 7.5h12.974c.576 0 1.059.435 1.119 1.007zM8.625 10.5a.375.375 0 11-.75 0 .375.375 0 01.75 0zm7.5 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" /></svg><p style="font-size:0.9375rem;font-weight:600;">Your bag is empty</p><button class="btn-primary btn-sm" onclick="CartDrawer._close()">Continue shopping</button></div>`;
      if (footer) footer.style.display = 'none';
      return;
    }
    if (footer) footer.style.display = '';
    body.innerHTML = state.items.map(item => `<div class="cart-item" data-key="${item.key}"><div class="cart-item-img">${item.image ? `<img src="${item.image}" alt="${item.title}" width="72" height="72" loading="lazy">` : ''}</div><div class="cart-item-info"><p class="cart-item-title">${item.product_title}</p>${item.variant_title && item.variant_title !== 'Default Title' ? `<p class="cart-item-variant">${item.variant_title}</p>` : ''}<p class="cart-item-price">${formatMoney(item.final_line_price)}</p><div class="cart-qty-pill"><button data-qty-dec data-key="${item.key}" data-qty="${item.quantity}" aria-label="Remove one"><svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14"/></svg></button><span>${item.quantity}</span><button data-qty-inc data-key="${item.key}" data-qty="${item.quantity}" aria-label="Add one"><svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M12 5v14M5 12h14"/></svg></button></div>${item.properties && item.properties._free_kit ? `<span class="cart-free-badge">FREE with bundle</span>` : `<button class="cart-remove-btn" data-remove data-key="${item.key}" ${item.properties && item.properties._free_kit ? 'data-free-kit="true"' : ''}>Remove</button>`}</div></div>`).join('');
    const subtotalEl = drawer.querySelector('[data-cart-subtotal]');
    if (subtotalEl) subtotalEl.textContent = formatMoney(state.total_price);
    $$('[data-qty-dec]', body).forEach(btn => { btn.addEventListener('click', async () => { const key = btn.dataset.key; const qty = parseInt(btn.dataset.qty, 10) - 1; await Cart.changeItem({ key, quantity: Math.max(0, qty) }); }); });
    $$('[data-qty-inc]', body).forEach(btn => { btn.addEventListener('click', async () => { const key = btn.dataset.key; const qty = parseInt(btn.dataset.qty, 10) + 1; await Cart.changeItem({ key, quantity: qty }); }); });
    $$('[data-remove]', body).forEach(btn => {
      btn.addEventListener('click', async () => {
        if (btn.dataset.freeKit === 'true') {
          const cartState = Cart.getState();
          const hasBundlePairs = cartState.items.some(i => i.properties && i.properties._bundle === 'duo');
          if (hasBundlePairs) { Toast.show('The free kit is included with your 2-pair bundle. Remove a pair first.'); return; }
        }
        await Cart.removeItem(btn.dataset.key);
      });
    });
  }
  function formatDollarPrice(priceStr) {
    const amount = parseFloat(priceStr) || 0;
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(amount);
  }
  function showUpsellVariantPopup(product, onConfirm) {
    const existing = document.getElementById('upsell-variant-popup');
    if (existing) existing.remove();
    const overlay = document.createElement('div');
    overlay.id = 'upsell-variant-popup';
    overlay.className = 'upsell-variant-popup-overlay';
    const sheet = document.createElement('div');
    sheet.className = 'upsell-variant-popup-sheet';
    // Shopify's /products.json shape: product.options is an array of
    // {name, values} objects (not strings), and each variant carries its
    // selections as option1/option2/option3 fields (not a variant.options
    // array). Using the wrong shape here threw inside this .map() before
    // the popup ever rendered, so clicking "Add" on any multi-variant
    // product (e.g. SnapMags) silently did nothing.
    // Selection state is also handled via addEventListener + a CSS class
    // rather than inline onclick="..." strings — the previous version built
    // onclick attributes containing escaped quotes inside a double-quoted
    // HTML attribute, which the HTML parser cut short, dumping raw JS as
    // visible button text instead of running it.
    const optionHTML = product.options.map((opt, i) => {
      const optName = typeof opt === 'string' ? opt : opt.name;
      const values = [...new Set(product.variants.map(v => v[`option${i + 1}`]))];
      return `<div class="upsell-variant-group"><p class="upsell-variant-group-label">${optName}</p><div class="upsell-variant-values" data-option="${i}">${values.map(v => `<button type="button" class="upsell-variant-pill" data-opt-idx="${i}" data-val="${v}">${v}</button>`).join('')}</div></div>`;
    }).join('');
    const img = product.images[0];
    sheet.innerHTML = `<div class="upsell-variant-popup-header">${img ? `<img src="${img.src.replace(/(\.[a-z]+)$/, '_100x100$1')}" alt="${product.title}" width="56" height="56" class="upsell-variant-popup-img">` : ''}<div><p class="upsell-variant-popup-title">${product.title}</p><p class="upsell-variant-popup-price">${formatDollarPrice(product.variants[0].price)}</p></div><button type="button" class="upsell-variant-popup-close" aria-label="Close">&times;</button></div>${optionHTML}<button type="button" id="upsell-confirm-btn" class="btn-primary" style="width:100%;margin-top:0.5rem;">Add to bag</button>`;
    overlay.appendChild(sheet);
    document.body.appendChild(overlay);
    overlay.addEventListener('click', e => { if (e.target === overlay) overlay.remove(); });
    sheet.querySelector('.upsell-variant-popup-close').addEventListener('click', () => overlay.remove());
    function getSelectedVals() {
      return product.options.map((_, i) => { const activeBtn = sheet.querySelector(`[data-option="${i}"] .is-selected`); return activeBtn ? activeBtn.dataset.val : null; });
    }
    // A value is pickable if some variant carrying it, combined with
    // whatever is already selected in every OTHER group, is in stock.
    // Recomputed after every click so choosing e.g. Silver correctly
    // greys out Square if Silver+Square only exists as a sold-out variant
    // — this is what stops a customer from ever landing on and adding a
    // combination Shopify itself reports as unavailable.
    function updateAvailability() {
      const selectedVals = getSelectedVals();
      sheet.querySelectorAll('.upsell-variant-pill').forEach(btn => {
        const optIdx = parseInt(btn.dataset.optIdx, 10);
        const val = btn.dataset.val;
        const available = product.variants.some(v => v.available && v[`option${optIdx + 1}`] === val && selectedVals.every((sel, j) => j === optIdx || sel === null || v[`option${j + 1}`] === sel));
        btn.classList.toggle('is-soldout', !available);
        btn.disabled = !available;
        btn.setAttribute('aria-disabled', String(!available));
      });
    }
    sheet.querySelectorAll('.upsell-variant-pill').forEach(btn => {
      btn.addEventListener('click', () => {
        if (btn.disabled) return;
        btn.closest('[data-option]').querySelectorAll('.upsell-variant-pill').forEach(b => b.classList.remove('is-selected'));
        btn.classList.add('is-selected');
        updateAvailability();
      });
    });
    updateAvailability();
    document.getElementById('upsell-confirm-btn').addEventListener('click', async () => {
      const selectedVals = getSelectedVals();
      if (selectedVals.some(v => v === null)) { Toast.show('Please select an option for every choice above'); return; }
      const variant = product.variants.find(v => v.available && selectedVals.every((val, i) => v[`option${i + 1}`] === val));
      if (!variant) { Toast.show('Sorry, that combination is out of stock'); return; }
      overlay.remove();
      await onConfirm(variant.id);
    });
  }
  // The upsell product catalog barely changes within a single browsing
  // session, but renderUpsells() runs on every cart mutation (add, qty
  // change, remove, drawer open). Fetch the candidate list once per page
  // load and reuse it, instead of re-fetching the same /products.json on
  // every single cart change.
  let upsellCatalogPromise = null;
  function getUpsellCatalog() {
    if (!upsellCatalogPromise) {
      upsellCatalogPromise = window.fetch('/collections/all/products.json?limit=8')
        .then(res => res.json())
        .then(json => json.products || [])
        .catch(() => { upsellCatalogPromise = null; return []; });
    }
    return upsellCatalogPromise;
  }
  async function renderUpsells(cartState) {
    const upsellEl = drawer.querySelector('.cart-upsell');
    if (!upsellEl) return;
    try {
      const allProds = await getUpsellCatalog();
      const prods = allProds.filter(p => !cartState.items.some(i => i.product_id === p.id)).slice(0, 4);
      if (!prods.length) { upsellEl.style.display = 'none'; return; }
      const heading = cartState.item_count === 0 ? 'You might like this' : 'Complete your look';
      upsellEl.innerHTML = `<p class="cart-upsell-heading">${heading}</p><div class="cart-upsell-strip">${prods.map(p => { const img = p.images[0]; const price = formatDollarPrice(p.variants[0].price); const hasVars = p.variants.length > 1; const vid = p.variants[0].id; const imgSrc = img ? img.src.replace(/(\.[a-z]+)$/, '_200x200$1') : ''; return `<div class="cart-upsell-item" data-product-id="${p.id}"><div class="cart-upsell-img">${imgSrc ? `<img src="${imgSrc}" alt="${p.title}" width="200" height="200" loading="lazy">` : ''}</div><div class="cart-upsell-details"><p class="cart-upsell-name">${p.title}</p><p class="cart-upsell-price">${price}</p></div><button class="cart-upsell-btn" data-vid="${vid}" data-has-variants="${hasVars}" data-prod-index="${prods.indexOf(p)}">Add</button></div>`; }).join('')}</div>`;
      upsellEl.style.display = '';
      upsellEl.querySelectorAll('.cart-upsell-btn').forEach(btn => {
        btn.addEventListener('click', async () => {
          const hasVariants = btn.dataset.hasVariants === 'true';
          const idx = parseInt(btn.dataset.prodIndex, 10);
          const product = prods[idx];
          async function doAdd(variantId) {
            try { btn.textContent = '...'; await Cart.addItem({ variantId: parseInt(variantId, 10), quantity: 1 }); btn.textContent = 'Added'; setTimeout(() => { btn.textContent = 'Add'; }, 2000); }
            catch { btn.textContent = 'Add'; }
          }
          if (hasVariants && product) showUpsellVariantPopup(product, doAdd);
          else await doAdd(btn.dataset.vid);
        });
      });
    } catch { upsellEl.style.display = 'none'; }
  }
  window.CartDrawer = { _close: close };
  return { init, open, close };
})();

const EmailPopup = (() => {
  const STORAGE_KEY = 'allureis_popup_dismissed';
  const SESSION_KEY = 'allureis_popup_shown';
  // One signup per device. Set the moment a signup succeeds; from then on the
  // popup never opens again, uninvited or from the nub. Kept in both
  // localStorage and a year-long cookie so clearing one does not reset it.
  const CLAIMED_KEY = 'allureis_popup_claimed';
  let popup, overlay, trigger;
  let knownSubscriber = false;
  function hasClaimed() {
    try { if (localStorage.getItem(CLAIMED_KEY)) return true; } catch (_) {}
    return document.cookie.split('; ').indexOf(CLAIMED_KEY + '=1') !== -1;
  }
  function markClaimed() {
    try { localStorage.setItem(CLAIMED_KEY, '1'); localStorage.setItem(STORAGE_KEY, '1'); } catch (_) {}
    document.cookie = CLAIMED_KEY + '=1; path=/; max-age=31536000; SameSite=Lax';
  }
  // Signed up: the offer copy and the form (field, button, "Not right now.")
  // give way to the green check, the address the code is going to, and a way
  // to keep shopping. Codes are unique per subscriber and generated by Klaviyo
  // at send time, so there is nothing safe to show here; the email carries it.
  function showCode(email) {
    const form = popup.querySelector('[data-popup-form]');
    if (!form || popup.querySelector('[data-popup-code-line]')) return;
    form.style.display = 'none';
    popup.querySelectorAll('.popup-label, .popup-offer, .popup-sub').forEach(el => { el.style.display = 'none'; });
    const block = document.createElement('div');
    block.setAttribute('data-popup-code-line', '');
    block.innerHTML = alSuccessHTML(email, 'Your personal 10% code is on its way to your inbox.', true);
    form.insertAdjacentElement('afterend', block);
  }
  function init() {
    popup = document.getElementById('email-popup');
    overlay = document.getElementById('popup-overlay');
    trigger = document.getElementById('popup-trigger');
    if (!popup) return;
    // Already signed up on this device: no popup, no nub, no second signup.
    if (hasClaimed()) { if (trigger) trigger.style.display = 'none'; return; }
    // Someone Klaviyo already recognises (arrived from one of our emails, or
    // signed up before) is on the list already, so don't pitch them the list.
    if (window.klaviyo && typeof window.klaviyo.isIdentified === 'function') {
      Promise.resolve(window.klaviyo.isIdentified()).then(v => { knownSubscriber = !!v; }).catch(() => {});
    }
    const closeBtn = popup.querySelector('.popup-close');
    if (closeBtn) closeBtn.addEventListener('click', () => { if (closeBtn.classList.contains('is-locked')) return; dismiss(); });
    const dismissLink = popup.querySelector('[data-popup-dismiss]');
    if (dismissLink) dismissLink.addEventListener('click', () => { if (dismissLink.classList.contains('is-locked')) return; dismiss(); });
    if (overlay) overlay.addEventListener('click', () => { if (overlay.dataset.emailPopupLocked === 'true') return; if (popup.classList.contains('is-open')) dismiss(); });
    // Only a 'click' listener here — initDraggableNub() below owns real touch/
    // mouse interaction (it tells a drag from a tap via movement distance and
    // opens only on a true tap-and-release). A second unconditional touchend
    // listener used to live here and fire on ANY touchend including right
    // after dragging the nub, which is why sliding it used to open the popup.
    if (trigger) { trigger.addEventListener('click', openFromTrigger); }
    const form = popup.querySelector('[data-popup-form]');
    if (form) form.addEventListener('submit', handleSubmit);
    initDraggableNub();
    if (sessionStorage.getItem(SESSION_KEY)) return;
    if (!localStorage.getItem(STORAGE_KEY)) {
      setTimeout(open, 15000);
      // Don't arm exit-intent until the page has actually been seen for a
      // few seconds — otherwise an accidental mouse move toward the browser
      // chrome in the first moment of a visit triggers the popup before the
      // customer has had a chance to look at anything.
      setTimeout(() => { document.addEventListener('mouseleave', onExitIntent); }, 10000);
    }
  }
  function onExitIntent(e) { if (e.clientY <= 0) { document.removeEventListener('mouseleave', onExitIntent); open(); } }
  // Same "must see the offer before dismissing" pattern as the bundle upsell
  // popup's lockUpsellDismiss — close button, overlay click, and the decline
  // link are all inert and visibly greyed (with the same close-button
  // progress ring) for the first 5 seconds the popup is open.
  function lockEmailDismiss() {
    if (!popup) return;
    const closeBtn = popup.querySelector('.popup-close');
    const dismissLink = popup.querySelector('[data-popup-dismiss]');
    [closeBtn, dismissLink].forEach(el => { if (el) el.classList.add('is-locked'); });
    if (overlay) overlay.dataset.emailPopupLocked = 'true';
    setTimeout(() => {
      [closeBtn, dismissLink].forEach(el => { if (el) el.classList.remove('is-locked'); });
      if (overlay) delete overlay.dataset.emailPopupLocked;
    }, 5000);
  }
  // Manually opening via the nub tab is a deliberate action — no cooldown.
  // The lock only applies when the popup shows up uninvited (15s timer or
  // exit-intent), where the customer needs a moment to actually see the
  // offer before dismissing it.
  function openFromTrigger() { if (hasClaimed()) return; sessionStorage.setItem(SESSION_KEY, '1'); if (popup) popup.classList.add('is-open'); if (overlay) overlay.classList.add('is-open'); lockBodyScroll(); }
  function open() { if (localStorage.getItem(STORAGE_KEY) || hasClaimed() || knownSubscriber) return; sessionStorage.setItem(SESSION_KEY, '1'); if (popup) popup.classList.add('is-open'); if (overlay) overlay.classList.add('is-open'); if (trigger) trigger.classList.add('popup-is-open'); lockBodyScroll(); lockEmailDismiss(); }
  function close() { if (popup) popup.classList.remove('is-open'); if (overlay) overlay.classList.remove('is-open'); if (trigger) trigger.classList.remove('popup-is-open'); unlockBodyScroll(); }
  function dismiss() { close(); localStorage.setItem(STORAGE_KEY, '1'); }
  function showTrigger() {}
  function initDraggableNub() {
    if (!trigger) return;
    const NUB_SIDE_KEY = 'allureis_nub_side';
    const NUB_Y_KEY = 'allureis_nub_y';
    const DRAG_THRESHOLD = 10;
    let isDragging = false, startX = 0, startY = 0, hasMoved = false, currentSide = 'left', currentTopPct = 82;
    function applyPosition(side, topPct, animate) {
      const clampedY = Math.max(8, Math.min(88, topPct));
      trigger.style.transition = animate ? 'left 0.35s cubic-bezier(0.22,1,0.36,1), right 0.35s cubic-bezier(0.22,1,0.36,1), top 0.35s cubic-bezier(0.22,1,0.36,1), opacity 0.2s' : 'none';
      trigger.style.top = `${clampedY}%`;
      if (side === 'left') { trigger.style.left = '0'; trigger.style.right = 'auto'; trigger.classList.add('nub-left'); }
      else { trigger.style.right = '0'; trigger.style.left = 'auto'; trigger.classList.remove('nub-left'); }
      currentSide = side; currentTopPct = clampedY;
    }
    const savedSide = localStorage.getItem(NUB_SIDE_KEY) || 'left';
    const savedY = parseFloat(localStorage.getItem(NUB_Y_KEY) || '82');
    applyPosition(savedSide, savedY, false);
    function onStart(e) { isDragging = true; hasMoved = false; const pt = e.touches ? e.touches[0] : e; startX = pt.clientX; startY = pt.clientY; trigger.style.transition = 'none'; trigger.style.cursor = 'grabbing'; e.preventDefault(); }
    function onMove(e) {
      if (!isDragging) return;
      const pt = e.touches ? e.touches[0] : e;
      const dx = pt.clientX - startX, dy = pt.clientY - startY;
      if (Math.sqrt(dx * dx + dy * dy) >= DRAG_THRESHOLD) hasMoved = true;
      if (hasMoved) {
        const x = pt.clientX, y = pt.clientY;
        if (x < window.innerWidth / 2) { trigger.style.left = '0'; trigger.style.right = 'auto'; trigger.classList.add('nub-left'); }
        else { trigger.style.right = '0'; trigger.style.left = 'auto'; trigger.classList.remove('nub-left'); }
        const topPct = Math.max(8, Math.min(88, (y / window.innerHeight) * 100));
        trigger.style.top = `${topPct}%`;
      }
    }
    function onEnd(e) {
      if (!isDragging) return;
      isDragging = false; trigger.style.cursor = 'grab';
      if (!hasMoved) { openFromTrigger(); return; }
      const rect = trigger.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2, centerY = rect.top + rect.height / 2;
      const side = centerX < window.innerWidth / 2 ? 'left' : 'right';
      const topPct = Math.max(8, Math.min(88, (centerY / window.innerHeight) * 100));
      applyPosition(side, topPct, true);
      localStorage.setItem(NUB_SIDE_KEY, side);
      localStorage.setItem(NUB_Y_KEY, topPct.toFixed(1));
    }
    trigger.addEventListener('touchstart', onStart, { passive: false });
    window.addEventListener('touchmove', onMove, { passive: true });
    window.addEventListener('touchend', onEnd, { passive: true });
    trigger.addEventListener('mousedown', onStart);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onEnd);
    trigger.removeEventListener('click', openFromTrigger);
  }
  async function handleSubmit(e) {
    e.preventDefault();
    const input = popup.querySelector('input[type="email"]');
    const btn = popup.querySelector('[data-popup-submit]');
    if (!input || !input.value.trim()) return;
    const email = input.value.trim();
    const originalText = btn ? btn.textContent : '';
    if (btn) { btn.textContent = 'Subscribing...'; btn.disabled = true; }
    const klaviyo = window.__klaviyoConfig || {};
    if (!klaviyo.listId || !klaviyo.publicKey) { if (btn) { btn.textContent = 'That did not go through. Try again.'; btn.disabled = false; } return; }
    try {
      // Explicit consent on a current revision. The old body (revision
      // 2023-12-15, no consent block) was answered 202 on 2026-09-11 and no
      // subscription followed, twice. This is the body proven to land profiles.
      const payload = { data: { type: 'subscription', attributes: { custom_source: 'Website Popup: 10% OFF', profile: { data: { type: 'profile', attributes: { email, subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } } } } } }, relationships: { list: { data: { type: 'list', id: klaviyo.listId } } } } };
      const res = await window.fetch(`https://a.klaviyo.com/client/subscriptions/?company_id=${klaviyo.publicKey}`, { method: 'POST', headers: { 'Content-Type': 'application/json', 'revision': '2026-07-15' }, body: JSON.stringify(payload) });
      if (!(res.ok || res.status === 202)) { console.warn('[Klaviyo] Subscribe returned', res.status); if (btn) { btn.textContent = 'That did not go through. Try again.'; btn.disabled = false; } return; }
    } catch (err) { console.warn('[Klaviyo] Subscribe error:', err); if (btn) { btn.textContent = originalText; btn.disabled = false; } return; }
    markClaimed();
    if (trigger) trigger.style.display = 'none';
    showCode(email);
  }
  return { init, open, close };
})();

const BeforeAfter = (() => {
  function init() { $$('[data-before-after]').forEach(initSlider); }
  function initSlider(container) {
    const after = container.querySelector('.before-after-after');
    const div = container.querySelector('.before-after-divider');
    const handle = container.querySelector('.before-after-handle');
    if (!after || !div || !handle) return;
    let pct = 50, dragging = false, rect = null, rafId = null, pendingX = null;
    after.style.willChange = 'clip-path';
    div.style.willChange = 'left';
    handle.style.willChange = 'left';
    function applyPosition() {
      rafId = null;
      if (pendingX === null || !rect) return;
      pct = Math.min(100, Math.max(0, ((pendingX - rect.left) / rect.width) * 100));
      after.style.clipPath = `inset(0 ${100 - pct}% 0 0)`;
      div.style.left = `${pct}%`;
      handle.style.left = `${pct}%`;
    }
    function setPosition(x) {
      pendingX = x;
      if (rafId === null) rafId = requestAnimationFrame(applyPosition);
    }
    function startDrag() {
      rect = container.getBoundingClientRect();
      dragging = true;
      container.classList.add('is-dragging');
    }
    function endDrag() {
      if (!dragging) return;
      dragging = false;
      container.classList.remove('is-dragging');
    }
    handle.addEventListener('mousedown', e => { startDrag(); e.preventDefault(); });
    container.addEventListener('mousedown', e => { startDrag(); setPosition(e.clientX); });
    window.addEventListener('mouseup', endDrag);
    window.addEventListener('mousemove', e => { if (dragging) setPosition(e.clientX); });
    handle.addEventListener('touchstart', e => { startDrag(); }, { passive: true });
    container.addEventListener('touchstart', e => { startDrag(); setPosition(e.touches[0].clientX); }, { passive: true });
    window.addEventListener('touchend', endDrag);
    window.addEventListener('touchcancel', endDrag);
    window.addEventListener('touchmove', e => { if (!dragging) return; e.preventDefault(); setPosition(e.touches[0].clientX); }, { passive: false });
    rect = container.getBoundingClientRect();
    setPosition(rect.left + container.offsetWidth / 2);
  }
  return { init };
})();

const FAQ = (() => {
  function init() { $$('.faq-item').forEach(item => { const trigger = item.querySelector('.faq-trigger'); if (!trigger) return; trigger.addEventListener('click', () => toggle(item)); }); }
  function toggle(item) {
    const isOpen = item.classList.contains('is-open');
    $$('.faq-item.is-open').forEach(el => { el.classList.remove('is-open'); const t = el.querySelector('.faq-trigger'); if (t) t.setAttribute('aria-expanded', 'false'); });
    if (!isOpen) { item.classList.add('is-open'); const trigger = item.querySelector('.faq-trigger'); if (trigger) trigger.setAttribute('aria-expanded', 'true'); }
  }
  return { init };
})();

const ProductPage = (() => {
  // Real variant option values only — shape is Round/Square (never Oval),
  // color is Silver/Gold, size is 6mm/8mm.
  const VARIANT_COLORS = ['Silver', 'Gold'];
  const VARIANT_SHAPES = ['Round', 'Square'];
  const VARIANT_SIZES = ['6mm', '8mm'];
  function randomVariantText() {
    const color = VARIANT_COLORS[Math.floor(Math.random() * VARIANT_COLORS.length)];
    const shape = VARIANT_SHAPES[Math.floor(Math.random() * VARIANT_SHAPES.length)];
    const size = VARIANT_SIZES[Math.floor(Math.random() * VARIANT_SIZES.length)];
    return `${color} ${shape} ${size}`;
  }
  const BUYER_PEOPLE = [
    { name: 'Brianna', city: 'Philadelphia' },
    { name: 'Maya', city: 'Austin' },
    { name: 'Jordan', city: 'Brooklyn' },
    { name: 'Sophie', city: 'Toronto' },
    { name: 'Aaliyah', city: 'Atlanta' },
    { name: 'Andy', city: 'McKinney' },
    { name: 'Quame', city: 'Baltimore' },
    { name: 'Juwaun', city: 'Bronx' },
    { name: 'Sarah', city: 'Denver' },
    { name: 'Diego', city: 'Miami' },
    { name: 'Olivia', city: 'Seattle' },
    { name: 'Marcus', city: 'Chicago' },
  ];
  function pickColorStyle(val) {
    const v = val.toLowerCase();
    if (v.includes('silver') || v.includes('grey') || v.includes('gray')) return '#C0C0C0';
    if (v.includes('gold') || v.includes('rose gold')) return '#C9A24B';
    if (v.includes('black')) return '#0A0A0A';
    if (v.includes('white')) return '#FFFFFF';
    if (v.includes('platinum')) return '#D8D8D8';
    if (v.includes('clear')) return '#F0F0F0';
    return null;
  }
  function swatchNeedsBorder(val) { const v = val.toLowerCase(); return v.includes('white') || v.includes('clear'); }
  function createInstance(sectionEl) {
    const sectionId = sectionEl.dataset.productSection;
    const productData = (window.__productData || {})[sectionId];
    if (!productData) return null;
    const state = { bundle: '', pair1: {}, pair2: {}, qty: 1, addingToCart: false, upsellAcknowledged: false, preorderAccepted: false };
    const buyBox = sectionEl.querySelector('[data-buy-box]');
    const stickyAtc = document.getElementById('sticky-atc');
    const variantPickerEl = sectionEl.querySelector('[data-variant-pickers]');
    const countdownEl = sectionEl.querySelector('[data-countdown]');
    const qtyDecEl = sectionEl.querySelector('[data-qty-dec]');
    const qtyIncEl = sectionEl.querySelector('[data-qty-inc]');
    const qtyValEl = sectionEl.querySelector('[data-qty-val]');
    const variantIdInput = document.getElementById('product-variant-id');
    const quantityInput = document.getElementById('product-quantity');
    const expressWrapper = document.querySelector('[data-express-wrapper]');
    const lowStockEl = sectionEl.querySelector('[data-low-stock]');
    const preorderEl = sectionEl.querySelector('[data-preorder]');
    const consentEl = sectionEl.querySelector('[data-preorder-consent]');
    const consentBox = sectionEl.querySelector('[data-preorder-accept]');
    const consentTextEl = sectionEl.querySelector('[data-preorder-consent-text]');
    const lowStockCountEl = sectionEl.querySelector('[data-low-stock-count]');
    // Above this, saying "only N left" is not urgency, it is noise.
    const LOW_STOCK_THRESHOLD = 10;
    const bundleProduct = productData.bundleProduct || null;
    const initSource = bundleProduct || productData;
    function findVariant(sel) { return productData.variants.find(v => productData.options.every((name, i) => sel[name] === v.options[i])) || productData.variants[0]; }
    function findBundleVariant(sel) { if (!bundleProduct) return findVariant(sel); return bundleProduct.variants.find(v => bundleProduct.options.every((name, i) => sel[name] === v.options[i])) || bundleProduct.variants[0]; }
    function getPairSource() { return (state.bundle === 'duo' && bundleProduct) ? bundleProduct : productData; }
    function getOptionValues(index) { const src = getPairSource(); const vals = new Set(); src.variants.forEach(v => { if (v.options[index]) vals.add(v.options[index]); }); return [...vals]; }
    // Keeps the express-checkout form honest. That form posts direct to
    // /checkout with one variant and one quantity, so it can only represent
    // the single tier. The kit and duo tiers are multi-line carts. Gating it
    // here is what stops a duo selection from silently checking out as one
    // pair of whatever variant the page happened to load with.
    function syncVariantInput() {
      const v1 = findVariant(state.pair1);
      if (variantIdInput && v1) variantIdInput.value = v1.id;
      if (quantityInput) quantityInput.value = state.qty;
      if (expressWrapper) {
        expressWrapper.hidden = !(state.bundle === 'single' && isSelectionComplete() && v1 && v1.available);
      }
      // Real low-stock, read from the variant's own inventory. Only shown once
      // a style is actually picked, since "only N left" is meaningless before
      // we know which N. Replaces a hardcoded 32.
      if (lowStockEl) {
        const inv = v1 && typeof v1.inventory === 'number' ? v1.inventory : null;
        const show = isSelectionComplete() && inv !== null && inv > 0 && inv <= LOW_STOCK_THRESHOLD;
        lowStockEl.hidden = !show;
        if (show && lowStockCountEl) lowStockCountEl.textContent = inv;
      }
      // Pre-order notice. Mutually exclusive with low stock, since a pre-order
      // variant is at zero by definition.
      const showPreorder = isSelectionComplete() && isPreorder(v1);
      if (preorderEl) preorderEl.hidden = !showPreorder;
      if (consentEl) consentEl.hidden = !showPreorder;
      // Consent is per-selection. Switching to a different style has to
      // re-earn it, otherwise a tick on one variant would silently carry
      // over to another.
      if (!showPreorder && state.preorderAccepted) {
        state.preorderAccepted = false;
        if (consentBox) consentBox.checked = false;
      }
    }
    function isOptionAvailable(optName, optValue, pairKey) {
      const src = getPairSource();
      const optIdx = src.options.indexOf(optName);
      const pairSel = state[pairKey] || {};
      return src.variants.some(v => {
        if (v.options[optIdx] !== optValue) return false;
        if (!v.available) return false;
        return src.options.every((name, i) => { if (name === optName) return true; const cur = pairSel[name]; return !cur || v.options[i] === cur; });
      });
    }
    function findGalleryVariant() {
      const sel = state.pair1;
      const opts = productData.options || [];
      if (opts.length && opts.every(name => sel[name])) {
        const exact = productData.variants.find(v => opts.every((name, i) => v.options[i] === sel[name]));
        if (exact) return exact;
      }
      // Color + Shape selected but Size not yet chosen: still preview the
      // matching color/shape combination using the first variant that
      // matches on those two options, pulling its real Shopify image.
      const colorName = opts.find(n => n.toLowerCase().includes('color'));
      const shapeName = opts.find(n => n.toLowerCase().includes('shape'));
      if (colorName && shapeName && sel[colorName] && sel[shapeName]) {
        const ci = opts.indexOf(colorName), si = opts.indexOf(shapeName);
        return productData.variants.find(v => v.options[ci] === sel[colorName] && v.options[si] === sel[shapeName]) || null;
      }
      return null;
    }
    function updateGalleryImage() {
      const v1 = findGalleryVariant();
      if (!v1 || !v1.image) return;
      const mainImg = sectionEl.querySelector('#gallery-main-img');
      if (!mainImg || mainImg.src.includes(v1.image)) return;
      mainImg.style.opacity = '0';
      // Drop the static srcset rendered by Liquid for the initial featured
      // image — it only describes that one image, so leaving it in place
      // after a variant swap would let the browser pick a stale srcset
      // candidate over the new src at some viewport widths.
      mainImg.removeAttribute('srcset');
      mainImg.src = v1.image;
      mainImg.onload = () => { mainImg.style.transition = 'opacity 0.25s'; mainImg.style.opacity = '1'; };
      sectionEl.querySelectorAll('.gallery-thumb').forEach(t => { const thumbSrc = (t.dataset.src || '').split('?')[0]; const varSrc = v1.image.split('?')[0]; t.classList.toggle('is-active', thumbSrc.includes(varSrc) || varSrc.includes(thumbSrc)); });
    }
    function updateATCState() {
      const selectionsComplete = isSelectionComplete();
      const v1 = selectionsComplete ? findVariant(state.pair1) : null;
      const available = v1 ? v1.available : false;
      const allATCBtns = [...sectionEl.querySelectorAll('[data-atc]'), ...(stickyAtc ? stickyAtc.querySelectorAll('[data-atc]') : [])];
      allATCBtns.forEach(btn => {
        if (!selectionsComplete) { btn.disabled = false; btn.classList.add('is-selecting'); btn.classList.remove('is-out-of-stock'); btn.textContent = 'SELECT YOUR STYLE'; return; }
        btn.classList.remove('is-selecting');
        btn.disabled = !available;
        if (available && isPreorder(v1) && !state.preorderAccepted) {
          btn.disabled = true;
          btn.classList.remove('is-out-of-stock');
          btn.textContent = 'Confirm the pre-order to continue';
          return;
        }
        if (!available) { btn.classList.add('is-out-of-stock'); btn.textContent = 'Out of Stock'; }
        else { btn.classList.remove('is-out-of-stock'); updateATCPrice(); }
      });
    }
    function updateATCPrice() {
      if (!isSelectionComplete()) return;
      const v1 = findVariant(state.pair1);
      if (!v1) return;
      let total = v1.price;
      let label = isPreorder(v1) ? 'Pre-order' : 'Add to bag';
      if (state.bundle === 'kit') { const kp = calcKitPricing(v1); total = kp.price * state.qty; }
      else if (state.bundle === 'duo' && bundleProduct) { const e1 = findBundleVariant(state.pair1) || bundleProduct.variants[0]; const e2 = findBundleVariant(state.pair2) || bundleProduct.variants[0]; total = calcBundlePrice(e1, e2) * state.qty; }
      else if (state.bundle === 'duo') { const v2 = findVariant(state.pair2) || v1; total = calcBundlePrice(v1, v2) * state.qty; }
      else { total = v1.price * state.qty; }
      const priceStr = formatMoney(total, productData.currency);
      const allATCBtns = [...sectionEl.querySelectorAll('[data-atc]'), ...(stickyAtc ? stickyAtc.querySelectorAll('[data-atc]') : [])];
      allATCBtns.forEach(btn => { if (!btn.disabled) { btn.textContent = `${label} · ${priceStr}`; btn.dataset.atcLabel = `${label} · ${priceStr}`; } });
    }
    function updateMainPrice() {
      const priceEl = sectionEl.querySelector('[data-product-price]');
      if (!priceEl) return;
      if (!state.bundle) { priceEl.textContent = `From ${formatMoney(productData.priceMin || productData.variants[0]?.price || 0, productData.currency)}`; return; }
      if (state.bundle === 'kit') {
        const kitV = Object.keys(state.pair1).length > 0 ? findVariant(state.pair1) : productData.variants[0];
        const kp = calcKitPricing(kitV);
        priceEl.textContent = formatMoney(kp.price, productData.currency);
        return;
      }
      if (state.bundle === 'duo') {
        const v1 = Object.keys(state.pair1).length > 0 ? findVariant(state.pair1) : null;
        const v2 = Object.keys(state.pair2).length > 0 ? findVariant(state.pair2) : null;
        if (v1 && v2) { const pricing = calcBundlePricing(v1, v2); priceEl.textContent = formatMoney(pricing.price, productData.currency); }
        else if (v1) { priceEl.textContent = `From ${formatMoney(v1.price, productData.currency)}`; }
        else { priceEl.textContent = `From ${formatMoney(productData.priceMin || productData.variants[0]?.price || 0, productData.currency)}`; }
        return;
      }
      const v1 = Object.keys(state.pair1).length > 0 ? findVariant(state.pair1) : null;
      if (v1) priceEl.textContent = formatMoney(v1.price, productData.currency);
      else priceEl.textContent = `From ${formatMoney(productData.priceMin || productData.variants[0]?.price || 0, productData.currency)}`;
    }
    // A variant Shopify will still sell at zero stock (inventory policy set to
    // CONTINUE) is a pre-order, not a normal in-stock buy. Shopify reports it
    // as available:true, so without this check the page would promise the
    // usual 1 to 3 day dispatch on something that has to be made first.
    function isPreorder(v) {
      if (!v || !v.available) return false;
      // Server-decided flag. Covers bundle variants that hold their own stock
      // number while depending on an oversold component, which the inventory
      // check below cannot see. Falls back to inventory for older payloads.
      if (typeof v.preorder === 'boolean') return v.preorder;
      return !!(typeof v.inventory === 'number' && v.inventory <= 0);
    }
    // The shipping window shown on the page, reused verbatim on the order so
    // the customer and the packing slip can never disagree.
    function preorderShipText() {
      const el = sectionEl.querySelector('[data-preorder] span');
      const t = el && el.textContent ? el.textContent.trim() : '';
      return t || 'Pre-order, ships in 14 to 18 days.';
    }
    // Builds a cart line, tagging it as a pre-order when the variant is one.
    // The property name has no leading underscore on purpose: underscored
    // properties are hidden, and this one needs to be visible on the Shopify
    // order, in exports, and to the customer at checkout.
    function pairLine(v, quantity, extra) {
      const props = Object.assign({}, extra || {});
      if (isPreorder(v)) {
        props['Pre-order'] = preorderShipText();
        // The exact wording the customer agreed to, stored verbatim, so a
        // later change to the theme setting cannot rewrite what they saw.
        const wording = consentTextEl && consentTextEl.textContent ? consentTextEl.textContent.trim() : '';
        props['Pre-order accepted'] = wording || 'Customer confirmed pre-order terms.';
      }
      const line = { variantId: v.id, quantity: quantity };
      if (Object.keys(props).length) line.properties = props;
      return line;
    }
    // A cart must be entirely pre-order or entirely in stock, never both.
    // Mixing them forces either a split shipment or an in-stock item held
    // hostage for weeks, and the customer agreed to neither. The free and
    // half-price kit lines ride along with whatever else is in the cart, so
    // they are exempt from the check.
    function cartConflictsWith(addingPreorder) {
      const st = Cart.getState();
      const items = (st && st.items) || [];
      return items.some(it => {
        const props = it.properties || {};
        if (props._free_kit || props._half_off_kit) return false;
        return !!props['Pre-order'] !== addingPreorder;
      });
    }
    // 'preorder' | 'instock' | 'mixed' for whatever is about to be added.
    function pendingPreorderKind(extraVariants) {
      const vs = [];
      const v1 = findVariant(state.pair1);
      if (v1) vs.push(v1);
      if (state.bundle === 'duo') { const v2 = findVariant(state.pair2); if (v2) vs.push(v2); }
      (extraVariants || []).forEach(v => { if (v) vs.push(v); });
      if (!vs.length) return 'instock';
      const pre = vs.filter(isPreorder).length;
      if (pre === 0) return 'instock';
      if (pre === vs.length) return 'preorder';
      return 'mixed';
    }
    // Returns true when the add should be stopped, and tells the customer why.
    function blockedByPreorderRule(extraVariants) {
      const kind = pendingPreorderKind(extraVariants);
      if (kind === 'mixed') {
        Toast.show('One of those styles is a pre-order. Pre-orders ship on their own, so please order them separately.');
        return true;
      }
      if (cartConflictsWith(kind === 'preorder')) {
        Toast.show(kind === 'preorder'
          ? 'Your bag has in-stock items. Check those out first, then place the pre-order.'
          : 'Your bag has a pre-order in it. Check that out first, then order in-stock items.');
        return true;
      }
      return false;
    }
    function calcBundlePricing(v1, v2) {
      const p1 = (v1 && v1.price) || 0;
      const p2 = (v2 && v2.price) || 0;
      // The pairs are charged at full price. The saving on this tier is the
      // stem kit, given away by the "Buy 2 Pairs, Get a Free Stem Kit"
      // automatic discount in Shopify.
      //
      // This used to read `original - 1000`, which advertised $10 off the
      // pairs. No discount in Shopify ever took that $10 off, so the page
      // showed $49.99 while checkout charged $59.98 on every 2-pair order.
      // Verified against orders #667152603 and #667153203, both charged
      // $59.98 with the pairs at full price. Do not reintroduce a discount
      // here without a matching Shopify discount, and note that Shopify
      // applies only ONE product-class automatic discount per order, so a
      // second one cannot stack with the free kit.
      const price = p1 + p2;
      const savings = productData.kitVariantPrice || 2499;
      const original = price + savings;
      return { price, original, savings };
    }
    function calcKitPricing(v1) {
      const pairPrice = (v1 && v1.price) || 0;
      const kitFullPrice = (productData.kitVariantPrice || 2499);
      // Derived from what Shopify actually charges, not hardcoded. The
      // "Buy 1 Pair, Get Stem Kit 50% Off" automatic discount takes
      // floor(kitFullPrice / 2) off the kit, so the kit bills at the
      // remainder. Verified in a live cart: a $29.99 pair plus a $24.99 kit
      // charges $42.49, with the kit discounted by $12.49 to $12.50.
      //
      // This used to return a hardcoded 4299 / 4799, which showed $42.99
      // against a real charge of $42.49 and claimed a $11.99 saving when the
      // true saving is $12.49.
      const kitDiscount = Math.floor(kitFullPrice / 2);
      const price = pairPrice + (kitFullPrice - kitDiscount);
      const original = pairPrice + kitFullPrice;
      const savings = kitDiscount;
      return { price, original, savings };
    }
    function calcBundlePrice(v1, v2) { return calcBundlePricing(v1, v2).price; }
    // A product has no real style choice when it carries no options, or its
    // only "option" is Shopify's synthetic single Default Title variant
    // (e.g. the replacement kit). Such products are a complete selection
    // the instant their bundle row is chosen — no swatch step required.
    function hasRealOptions(src) {
      if (!src) return false;
      const opts = src.options || [];
      if (!opts.length) return false;
      if (src.variants && src.variants.length === 1 && src.variants[0].title === 'Default Title') return false;
      return true;
    }
    function isSelectionComplete() {
      if (!state.bundle) return false;
      const src = getPairSource();
      if (!hasRealOptions(src)) return true;
      const requiredOptions = src.options || [];
      if (!requiredOptions.length) return false;
      const pair1Complete = requiredOptions.every(n => state.pair1[n]);
      const pair2Complete = state.bundle === 'duo' ? requiredOptions.every(n => state.pair2[n]) : true;
      return pair1Complete && pair2Complete;
    }
    function minVariantPrice(list) {
      if (!list || !list.length) return 0;
      const avail = list.filter(v => v.available);
      const pool = avail.length ? avail : list;
      return Math.min(...pool.map(v => v.price));
    }
    function isPairCompleteFor(src, pairSel) {
      if (!hasRealOptions(src)) return true;
      const opts = (src && src.options) || [];
      return opts.length > 0 && opts.every(n => pairSel && pairSel[n]);
    }
    function updateBundleUI() {
      sectionEl.querySelectorAll('[data-bundle]').forEach(btn => { const isSelected = state.bundle !== '' && btn.dataset.bundle === state.bundle; btn.classList.toggle('is-selected', isSelected); btn.setAttribute('aria-pressed', isSelected ? 'true' : 'false'); });
      const variant = findVariant(state.pair1) || productData.variants[0];
      if (!variant) return;
      const singlePrice = variant.price;
      const duoV1 = bundleProduct ? (findBundleVariant(state.pair1) || bundleProduct.variants[0]) : variant;
      const duoV2 = bundleProduct ? (findBundleVariant(state.pair2) || bundleProduct.variants[0]) : (findVariant(state.pair2) || productData.variants[0]);
      const bundlePricing = calcBundlePricing(duoV1, duoV2);
      // Completeness is checked against each row's own product (not the
      // currently-active bundle), and against ALL required options — not
      // just a hardcoded "size" option — so this works for any product,
      // including ones without a literal size option (e.g. the kit).
      const pair1CompleteSelf = isPairCompleteFor(productData, state.pair1);
      const pair2CompleteSelf = isPairCompleteFor(productData, state.pair2);
      const duoSrc = bundleProduct || productData;
      const pair1CompleteDuo = isPairCompleteFor(duoSrc, state.pair1);
      const pair2CompleteDuo = isPairCompleteFor(duoSrc, state.pair2);
      sectionEl.querySelectorAll('[data-bundle-price]').forEach(el => {
        const which = el.dataset.bundlePrice;
        if (which === 'single') {
          if (!pair1CompleteSelf) { el.textContent = 'From ' + formatMoney(minVariantPrice(productData.variants), productData.currency); return; }
          const singleCompare = variant && variant.compareAtPrice && variant.compareAtPrice > singlePrice ? variant.compareAtPrice : null;
          if (singleCompare) el.innerHTML = `<span class="bundle-price-current">${formatMoney(singlePrice, productData.currency)}</span><span class="bundle-price-original">${formatMoney(singleCompare, productData.currency)}</span>`;
          else el.textContent = formatMoney(singlePrice, productData.currency);
        } else if (which === 'kit') {
          if (!pair1CompleteSelf) { const kp = calcKitPricing({ price: minVariantPrice(productData.variants) }); el.textContent = 'From ' + formatMoney(kp.price, productData.currency); return; }
          const kitV = findVariant(state.pair1) || productData.variants[0];
          const kp = calcKitPricing(kitV);
          el.innerHTML = `<span class="bundle-price-current">${formatMoney(kp.price, productData.currency)}</span><span class="bundle-price-original">${formatMoney(kp.original, productData.currency)}</span>`;
        } else {
          if (!pair1CompleteDuo || !pair2CompleteDuo) {
            const minP = minVariantPrice(duoSrc.variants);
            const lowestPricing = calcBundlePricing({ price: minP }, { price: minP });
            el.textContent = 'From ' + formatMoney(lowestPricing.price, productData.currency);
            return;
          }
          el.innerHTML = `<span class="bundle-price-current">${formatMoney(bundlePricing.price, productData.currency)}</span><span class="bundle-price-original">${formatMoney(bundlePricing.original, productData.currency)}</span>`;
        }
      });
      const kitBlock = sectionEl.querySelector('#kit-variant-block');
      if (kitBlock) kitBlock.style.display = state.bundle === 'single' ? '' : 'none';
    }
    // Required selection order: Color, then Shape, then Size. Any option
    // name not matching one of these (e.g. a kit's own option) falls back
    // to its natural position, after the three.
    function optionOrderRank(name) {
      const n = name.toLowerCase();
      if (n.includes('color')) return 0;
      if (n.includes('shape')) return 1;
      if (n.includes('size')) return 2;
      return 3;
    }
    function renderPickerInner(pairKey, label) {
      const sel = state[pairKey];
      const src = getPairSource();
      const opts = src.options
        .map((name, i) => ({ name, values: getOptionValues(i) }))
        .sort((a, b) => optionOrderRank(a.name) - optionOrderRank(b.name));
      let priorComplete = true;
      return `<p class="pair-label">${label}</p>${opts.map(opt => {
        const isColorOpt = opt.name.toLowerCase().includes('color');
        const isComplete = !!sel[opt.name];
        const isLocked = !priorComplete;
        const isActive = !isLocked && !isComplete;
        const stepState = isLocked ? 'locked' : (isComplete ? 'complete' : 'active');
        const swatchesHTML = opt.values.map(val => {
          const colorStyle = pickColorStyle(val);
          const isColor = !!colorStyle && isColorOpt;
          const isSelected = sel[opt.name] === val;
          const needsBorder = isColor && swatchNeedsBorder(val);
          const available = isOptionAvailable(opt.name, val, pairKey);
          const isDisabled = !available || isLocked;
          if (isColor) return `<button class="color-swatch${isSelected ? ' is-selected' : ''}${!available ? ' is-soldout' : ''}${isLocked ? ' is-locked' : ''}" data-swatch data-pair="${pairKey}" data-opt-name="${opt.name}" data-opt-value="${val}" title="${val}${!available ? ' (sold out)' : ''}" aria-label="${val}${isSelected ? ' (selected)' : ''}${!available ? ' — sold out' : ''}" ${isDisabled ? 'aria-disabled="true"' : ''} ${isLocked ? 'disabled tabindex="-1"' : ''}><span class="color-swatch-inner${needsBorder ? ' swatch-border' : ''}" style="background:${colorStyle};"></span></button>`;
          return `<button class="pill-swatch${isSelected ? ' is-selected' : ''}${!available ? ' is-soldout' : ''}${isLocked ? ' is-locked' : ''}" data-swatch data-pair="${pairKey}" data-opt-name="${opt.name}" data-opt-value="${val}" ${isDisabled ? 'aria-disabled="true"' : ''} ${isLocked ? 'disabled tabindex="-1"' : ''}>${val}${!available ? '<span class="swatch-soldout-label"> (sold out)</span>' : ''}</button>`;
        }).join('');
        priorComplete = priorComplete && isComplete;
        return `<div class="variant-option is-${stepState}" data-opt-key="${opt.name}"><div class="variant-option-header"><span class="variant-option-name">${opt.name}<span class="required-asterisk" aria-hidden="true">*</span>${isComplete ? '<span class="step-check" aria-hidden="true">✓</span>' : ''}${isActive ? '<span class="step-badge">Select now</span>' : ''}</span><span class="variant-option-value">${sel[opt.name] || ''}</span></div><div class="swatch-row">${swatchesHTML}</div></div>`;
      }).join('')}`;
    }
    // Updates an already-rendered picker in place: swatch selected/locked/
    // soldout state, step locked/active/complete state, and the selected-
    // value label, via classList/attribute mutation only. No innerHTML
    // rewrite, no node removal or reinsertion — this is what runs on every
    // swatch click so the picker never flickers or resets.
    function patchVariantPickers() {
      const pickerEl = sectionEl.querySelector('.bundle-inline-picker');
      if (!pickerEl) return;
      const sides = pickerEl.querySelectorAll('[data-split-side]');
      const scopes = sides.length
        ? [...sides].map(s => ({ el: s, pairKey: s.dataset.splitSide }))
        : [{ el: pickerEl, pairKey: 'pair1' }];
      scopes.forEach(({ el, pairKey }) => {
        const sel = state[pairKey] || {};
        let priorComplete = true;
        el.querySelectorAll('.variant-option').forEach(optEl => {
          const optName = optEl.dataset.optKey;
          const isComplete = !!sel[optName];
          const isLocked = !priorComplete;
          const isActive = !isLocked && !isComplete;
          const stepState = isLocked ? 'locked' : (isComplete ? 'complete' : 'active');
          optEl.className = `variant-option is-${stepState}`;
          const nameEl = optEl.querySelector('.variant-option-name');
          const valueEl = optEl.querySelector('.variant-option-value');
          if (valueEl) valueEl.textContent = sel[optName] || '';
          if (nameEl) {
            let check = nameEl.querySelector('.step-check');
            let badge = nameEl.querySelector('.step-badge');
            if (isComplete) { if (!check) { check = document.createElement('span'); check.className = 'step-check'; check.setAttribute('aria-hidden', 'true'); check.textContent = '✓'; nameEl.appendChild(check); } }
            else if (check) check.remove();
            if (isActive) { if (!badge) { badge = document.createElement('span'); badge.className = 'step-badge'; badge.textContent = 'Select now'; nameEl.appendChild(badge); } }
            else if (badge) badge.remove();
          }
          optEl.querySelectorAll('[data-swatch]').forEach(swatch => {
            const optValue = swatch.dataset.optValue;
            const isSelected = sel[optName] === optValue;
            const available = isOptionAvailable(optName, optValue, pairKey);
            const isDisabled = !available || isLocked;
            swatch.classList.toggle('is-selected', isSelected);
            swatch.classList.toggle('is-soldout', !available);
            swatch.classList.toggle('is-locked', isLocked);
            if (isDisabled) swatch.setAttribute('aria-disabled', 'true'); else swatch.removeAttribute('aria-disabled');
            if (isLocked) { swatch.setAttribute('disabled', ''); swatch.setAttribute('tabindex', '-1'); } else { swatch.removeAttribute('disabled'); swatch.removeAttribute('tabindex'); }
            if (swatch.hasAttribute('title')) swatch.setAttribute('title', `${optValue}${!available ? ' (sold out)' : ''}`);
            swatch.setAttribute('aria-label', `${optValue}${isSelected ? ' (selected)' : ''}${!available ? ' (sold out)' : ''}`);
          });
          priorComplete = priorComplete && isComplete;
        });
      });
    }
    function renderVariantPickers() {
      const existingPicker = sectionEl.querySelector('.bundle-inline-picker');
      if (existingPicker) existingPicker.remove();
      if (!state.bundle) return;
      const selectedBtn = sectionEl.querySelector(`[data-bundle="${state.bundle}"]`);
      if (!selectedBtn) return;
      let pickerHTML = '';
      const kitImg = productData.freeKitImageUrl || 'https://cdn.shopify.com/s/files/1/0989/5090/6139/files/repl1.png?v=1768405171';
      // Kit card figures derived from the real kit price and the actual
      // "Buy 1 Pair, Get Stem Kit 50% Off" behaviour, not hardcoded. Shopify
      // takes floor(kitFull / 2) off, so a $24.99 kit bills at $12.50 with a
      // $12.49 discount. The card previously claimed "You save $12.50".
      const _kitFull = productData.kitVariantPrice || 2499;
      const _kitDisc = Math.floor(_kitFull / 2);
      const _kitHalf = _kitFull - _kitDisc;
      const _cur = productData.currency;
      const kitImgHTML = kitImg ? `<div class="free-kit-img"><img src="${kitImg}" alt="4pc Replacement Stem Kit" loading="lazy" width="64" height="64"></div>` : '<div class="free-kit-img free-kit-img--placeholder"></div>';
      if (state.bundle === 'single') {
        if (!hasRealOptions(productData)) { updateStickyAtc(); syncVariantInput(); updateGalleryImage(); updateATCState(); return; }
        pickerHTML = `<div class="variant-picker-block">${renderPickerInner('pair1', 'Choose Your Style')}</div>`;
      }
      else if (state.bundle === 'kit') {
        pickerHTML = `<div class="variant-picker-block">${renderPickerInner('pair1', 'Choose Your Style')}</div><div class="free-kit-card" aria-label="Stem kit at 50% off">${kitImgHTML}<div class="free-kit-body"><p class="free-kit-name">4pc Replacement Stem Kit</p><p class="free-kit-label">Stem Kit included at 50% off</p><div class="free-kit-price-row"><span class="free-kit-original">${formatMoney(_kitFull, _cur)}</span><span class="free-kit-now">${formatMoney(_kitHalf, _cur)}</span><span class="free-kit-badge">You save ${formatMoney(_kitDisc, _cur)}</span></div></div></div>`;
      } else {
        pickerHTML = `<div class="split-picker-card"><div class="split-picker-side split-picker-side--first" data-split-side="pair1">${renderPickerInner('pair1', 'Choose Your Style, First Pair')}</div><div class="split-picker-divider" aria-hidden="true"></div><div class="split-picker-side split-picker-side--second" data-split-side="pair2">${renderPickerInner('pair2', 'Choose Your Style, Second Pair')}</div></div><div class="free-kit-card" aria-label="Free stem kit included">${kitImgHTML}<div class="free-kit-body"><p class="free-kit-name">4pc Replacement Stem Kit</p><p class="free-kit-label">Included free with your order</p><div class="free-kit-price-row"><span class="free-kit-original">${formatMoney(_kitFull, _cur)}</span><span class="free-kit-now">${formatMoney(0, _cur)}</span><span class="free-kit-badge">You save ${formatMoney(_kitFull, _cur)}</span></div></div></div>`;
      }
      const pickerEl = document.createElement('div');
      // Grid-rows height animation (0fr -> 1fr) so the picker grows smoothly
      // into place instead of popping in and instantly shoving the Add to
      // Bag button down — same fix covers the entrance animation and the
      // layout-shift concern, they're one piece of code.
      pickerEl.className = 'bundle-inline-picker';
      pickerEl.innerHTML = `<div class="bundle-inline-picker-inner">${pickerHTML}</div>`;
      selectedBtn.insertAdjacentElement('afterend', pickerEl);
      requestAnimationFrame(() => requestAnimationFrame(() => pickerEl.classList.add('is-open')));
      const activeSlot = pickerEl;
      activeSlot.querySelectorAll('[data-swatch]').forEach(swatch => {
        swatch.addEventListener('click', (e) => {
          if (swatch.classList.contains('is-soldout') || swatch.getAttribute('aria-disabled') === 'true') {
            e.preventDefault(); e.stopPropagation();
            const tip = swatch.querySelector('.swatch-oos-tip') || (() => { const t = document.createElement('span'); t.className = 'swatch-oos-tip'; t.textContent = 'Out of stock'; t.setAttribute('aria-live', 'polite'); swatch.appendChild(t); return t; })();
            tip.classList.add('is-visible');
            setTimeout(() => tip.classList.remove('is-visible'), 1800);
            return;
          }
          state[swatch.dataset.pair][swatch.dataset.optName] = swatch.dataset.optValue;
          // Patch the existing picker DOM in place (no rebuild/flicker) so
          // locked/active/complete step states recompute as the required
          // Color, then Shape, then Size order progresses.
          updateBundleUI(); patchVariantPickers(); updateStickyAtc(); syncVariantInput(); updateGalleryImage(); updateATCState(); updateATCPrice(); updateMainPrice();
        });
      });
      updateStickyAtc(); syncVariantInput(); updateGalleryImage(); updateATCState();
    }
    let stickyAtcScrollTicking = false;
    function onScrollStickyAtc() {
      if (!stickyAtc || stickyAtcScrollTicking) return;
      stickyAtcScrollTicking = true;
      requestAnimationFrame(() => {
        stickyAtcScrollTicking = false;
        const mainBtn = sectionEl.querySelector('.atc-btn');
        if (mainBtn) stickyAtc.classList.toggle('is-visible', mainBtn.getBoundingClientRect().bottom < 0);
        else if (buyBox) stickyAtc.classList.toggle('is-visible', buyBox.getBoundingClientRect().bottom < 80);
      });
    }
    function updateStickyAtc() {
      if (!stickyAtc) return;
      const detailsEl = stickyAtc.querySelector('[data-sticky-details]');
      const hasSelections = Object.keys(state.pair1).length > 0;
      if (!hasSelections) { if (detailsEl) detailsEl.textContent = ''; return; }
      let detailText = '';
      if (state.bundle === 'duo') {
        const v1 = bundleProduct ? (findBundleVariant(state.pair1) || bundleProduct.variants[0]) : (findVariant(state.pair1) || productData.variants[0]);
        const v2 = bundleProduct ? (findBundleVariant(state.pair2) || bundleProduct.variants[0]) : (findVariant(state.pair2) || productData.variants[0]);
        const t1 = v1 && v1.title !== 'Default Title' ? v1.title : '';
        const t2 = v2 && v2.title !== 'Default Title' ? v2.title : '';
        if (t1 || t2) detailText = `${t1}${t1 && t2 ? '  +  ' : ''}${t2}`;
      } else {
        const v1 = findVariant(state.pair1) || productData.variants[0];
        if (v1 && v1.title !== 'Default Title') detailText = productData.options.map((name, i) => state.pair1[name] ? `${state.pair1[name]}` : '').filter(Boolean).join(' · ');
      }
      if (detailsEl) detailsEl.textContent = detailText;
    }
    // Selecting a bundle from the upsell popup must do exactly what clicking
    // that bundle's own card on the page does, so the popup is never a
    // separate decision path with its own logic to drift out of sync.
    function selectBundle(key) {
      state.bundle = key;
      state.upsellAcknowledged = false;
      updateBundleUI(); renderVariantPickers(); updateStickyAtc(); syncVariantInput(); updateATCState(); updateATCPrice(); updateMainPrice();
    }
    // Builds a popup card by cloning the real .bundle-option button straight
    // out of the page's bundle selector (same name, same data-bundle-price
    // node, same markup) rather than hand-writing a duplicate row, so the
    // popup can never show a price that disagrees with the page. Adds a
    // tier thumbnail, the same imagery already used elsewhere on the page
    // for that tier, since the source card carries no image of its own.
    // ── Gold clearance add-on ──────────────────────────────────────────
    // Gold is dead stock (166 units against ~4 orders a month) and is not
    // being reordered, so it is priced at $14.99 to clear. Offered inside the
    // existing single-item upsell popup: adding it makes the cart two pairs,
    // which triggers the "Buy 2 Pairs, Get a Free Stem Kit" automatic
    // discount, so the kit line is added here too in order for that discount
    // to have something to apply to.
    function findGoldClearanceVariant() {
      const golds = (productData.variants || []).filter(v =>
        (v.options || []).indexOf('Gold') !== -1 && v.available && (v.inventory === undefined || v.inventory > 0));
      if (!golds.length) return null;
      // Prefer the size the customer already picked, so the add-on matches.
      const size = (state.pair1 || {}).Size;
      return golds.find(v => (v.options || []).indexOf(size) !== -1) || golds[0];
    }
    function buildGoldAddOnCard() {
      if (state.bundle !== 'single') return null;
      const gold = findGoldClearanceVariant();
      if (!gold) return null;
      const cur = productData.currency;
      const compare = gold.compareAtPrice && gold.compareAtPrice > gold.price
        ? `<span class="bundle-price-original">${formatMoney(gold.compareAtPrice, cur)}</span>` : '';
      const card = document.createElement('button');
      card.type = 'button';
      card.className = 'bundle-option upsell-gold-card';
      card.setAttribute('aria-label', `Add a Gold pair for ${formatMoney(gold.price, cur)}`);
      const imgSrc = gold.image || '';
      const imgHTML = imgSrc
        ? `<img class="upsell-card-img" src="${imgSrc}" alt="" width="48" height="48" loading="lazy">` : '';
      card.innerHTML = `${imgHTML}<span class="bundle-option-content"><span class="bundle-option-row1"><span class="bundle-option-name">Add a Gold pair</span><span class="bundle-option-price-col"><span class="bundle-price-current">${formatMoney(gold.price, cur)}</span>${compare}</span></span><span class="bundle-option-row2"><span class="bundle-option-sub">Final stock. 2 pairs gets the kit free.</span></span></span>`;
      card.addEventListener('click', async () => {
        if (state.addingToCart) return;
        const v1 = findVariant(state.pair1);
        if (!v1 || !v1.available) { Toast.show('This option is out of stock'); return; }
        if (blockedByPreorderRule([gold])) { closeBundleUpsell(); return; }
        state.addingToCart = true;
        state.upsellAcknowledged = true;
        closeBundleUpsell();
        setATCLoading(true);
        try {
          const items = [
            pairLine(v1, state.qty),
            pairLine(gold, 1, { _gold_clearance: 'true' })
          ];
          const kitId = productData.freeKitVariantId || productData.kitVariantId;
          if (kitId) items.push({ variantId: kitId, quantity: 1, properties: { _free_kit: 'true', _bundle: 'gold-addon' } });
          await Cart.addItems(items);
          Toast.show('Added to bag');
          CartDrawer.open();
        } catch (err) {
          Toast.show(err.message || 'Could not add to bag. Please try again.');
        } finally { state.addingToCart = false; setATCLoading(false); }
      });
      return card;
    }
    function buildUpsellCard(realBtn) {
      const clone = realBtn.cloneNode(true);
      clone.removeAttribute('aria-pressed');
      clone.classList.remove('is-selected');
      const bundleKey = clone.dataset.bundle;
      const imgSrc = bundleKey === 'single'
        ? ((productData.variants[0] && productData.variants[0].image) || '')
        : (productData.freeKitImageUrl || '');
      if (imgSrc) {
        const img = document.createElement('img');
        img.src = imgSrc;
        img.alt = '';
        img.width = 48;
        img.height = 48;
        img.loading = 'lazy';
        img.className = 'upsell-card-img';
        clone.insertBefore(img, clone.firstChild);
      }
      clone.addEventListener('click', () => {
        selectBundle(bundleKey);
        closeBundleUpsell();
        const bundleEl = sectionEl.querySelector('.bundle-selector');
        if (bundleEl) bundleEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
      return clone;
    }
    function openBundleUpsell() {
      const popup = document.getElementById('bundle-upsell-popup');
      const overlay = document.getElementById('popup-overlay');
      if (!popup) { state.upsellAcknowledged = true; handleATC(); return; }
      const optionsEl = popup.querySelector('[data-upsell-options]');
      if (optionsEl) {
        optionsEl.innerHTML = '';
        const otherBundleBtns = [...sectionEl.querySelectorAll('[data-bundle]')].filter(b => b.dataset.bundle !== 'single');
        otherBundleBtns.forEach(btn => optionsEl.appendChild(buildUpsellCard(btn)));
        const goldCard = buildGoldAddOnCard();
        if (goldCard) optionsEl.appendChild(goldCard);
      }
      popup.classList.add('is-open');
      if (overlay) overlay.classList.add('is-open');
      lockBodyScroll();
      lockUpsellDismiss(popup, overlay);
    }
    function closeBundleUpsell() {
      const popup = document.getElementById('bundle-upsell-popup');
      const overlay = document.getElementById('popup-overlay');
      if (popup) popup.classList.remove('is-open');
      if (overlay) overlay.classList.remove('is-open');
      unlockBodyScroll();
    }
    // The customer must actually see the offer before they can dismiss it:
    // close button, overlay click, and the decline link are all inert and
    // visibly greyed out for the first 3 seconds the popup is open.
    function lockUpsellDismiss(popup, overlay) {
      const closeBtn = popup.querySelector('[data-upsell-close]');
      const declineBtn = popup.querySelector('[data-upsell-decline]');
      [closeBtn, declineBtn].forEach(el => { if (el) el.classList.add('is-locked'); });
      if (overlay) overlay.dataset.upsellLocked = 'true';
      setTimeout(() => {
        [closeBtn, declineBtn].forEach(el => { if (el) el.classList.remove('is-locked'); });
        if (overlay) delete overlay.dataset.upsellLocked;
      }, 3000);
    }
    function wireBundleUpsell() {
      const popup = document.getElementById('bundle-upsell-popup');
      if (!popup) return;
      const closeBtn = popup.querySelector('[data-upsell-close]');
      const declineBtn = popup.querySelector('[data-upsell-decline]');
      if (closeBtn) closeBtn.addEventListener('click', () => { if (closeBtn.classList.contains('is-locked')) return; closeBundleUpsell(); });
      if (declineBtn) declineBtn.addEventListener('click', () => {
        if (declineBtn.classList.contains('is-locked')) return;
        state.upsellAcknowledged = true;
        closeBundleUpsell();
        handleATC();
      });
      const overlay = document.getElementById('popup-overlay');
      if (overlay) overlay.addEventListener('click', () => { if (overlay.dataset.upsellLocked === 'true') return; if (popup.classList.contains('is-open')) closeBundleUpsell(); });
    }
    async function handleATC() {
      if (state.addingToCart) return;
      if (!state.bundle) { const bundleEl = sectionEl.querySelector('.bundle-selector'); if (bundleEl) bundleEl.scrollIntoView({ behavior: 'smooth', block: 'center' }); Toast.show('Please choose your bundle first'); return; }
      const src = getPairSource();
      const requiredOptions = hasRealOptions(src) ? src.options : [];
      const pair1Missing = requiredOptions.filter(n => !state.pair1[n]);
      const pair2Missing = state.bundle === 'duo' ? requiredOptions.filter(n => !state.pair2[n]) : [];
      if (pair1Missing.length > 0 || pair2Missing.length > 0) {
        const inlinePicker = sectionEl.querySelector('.bundle-inline-picker');
        const splitCard = sectionEl.querySelector('.split-picker-card');
        const pickerEl = sectionEl.querySelector('[data-variant-pickers]');
        const shakeOptionsByNames = (container, optNames) => { if (!container) return; optNames.forEach(name => { const optEl = container.querySelector(`[data-opt-key="${name}"]`); if (optEl) { optEl.classList.add('option-error'); setTimeout(() => optEl.classList.remove('option-error'), 800); } }); };
        if (splitCard) {
          if (pair1Missing.length > 0) { const side = splitCard.querySelector('[data-split-side="pair1"]'); side && side.classList.add('picker-error'); shakeOptionsByNames(side, pair1Missing); setTimeout(() => side && side.classList.remove('picker-error'), 2000); }
          if (pair2Missing.length > 0) { const side = splitCard.querySelector('[data-split-side="pair2"]'); side && side.classList.add('picker-error'); shakeOptionsByNames(side, pair2Missing); setTimeout(() => side && side.classList.remove('picker-error'), 2000); }
          splitCard.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } else if (inlinePicker) { shakeOptionsByNames(inlinePicker, pair1Missing); inlinePicker.scrollIntoView({ behavior: 'smooth', block: 'center' }); }
        else if (pickerEl) { pickerEl.scrollIntoView({ behavior: 'smooth', block: 'center' }); pickerEl.style.transition = 'box-shadow 0.3s'; pickerEl.style.boxShadow = '0 0 0 2px var(--fg)'; setTimeout(() => { pickerEl.style.boxShadow = ''; }, 1200); }
        const missing = [...new Set([...pair1Missing, ...pair2Missing])];
        Toast.show(`Please select your ${missing.join(', ').toLowerCase()}`);
        return;
      }
      const v1check = findVariant(state.pair1);
      if (!v1check || !v1check.available) { Toast.show('This option is out of stock'); return; }
      if (state.bundle === 'duo') { const v2check = findVariant(state.pair2); if (!v2check || !v2check.available) { Toast.show('Your second pair selection is out of stock'); return; } }
      if (blockedByPreorderRule()) return;
      if (state.bundle === 'single' && !state.upsellAcknowledged) { openBundleUpsell(); return; }
      state.addingToCart = true;
      setATCLoading(true);
      try {
        if (state.bundle === 'kit') {
          const v1 = findVariant(state.pair1);
          if (!v1 || !v1.available) { Toast.show('This option is out of stock'); return; }
          // Add the REAL ($24.99) kit variant — not the $0 free-kit variant —
          // so the "Buy 1 Pair, Get Stem Kit 50% Off" Shopify automatic
          // discount can apply and charge the correct $12.50, instead of
          // silently giving the kit away for free.
          const kitId = productData.kitVariantId;
          const items = [pairLine(v1, state.qty)];
          if (kitId) items.push({ variantId: kitId, quantity: 1, properties: { _half_off_kit: 'true', _note: 'Stem kit half off' } });
          await Cart.addItems(items);
        } else if (state.bundle === 'duo' && bundleProduct) {
          const e1 = findBundleVariant(state.pair1);
          const e2 = findBundleVariant(state.pair2);
          const kit = productData.variants[0];
          if (!e1 || !e1.available) { Toast.show('First pair is out of stock'); return; }
          if (!e2 || !e2.available) { Toast.show('Second pair is out of stock'); return; }
          await Cart.addItems([pairLine(e1, state.qty), pairLine(e2 || e1, state.qty), { variantId: kit.id, quantity: 1, properties: { _free_kit: 'true' } }]);
        } else if (state.bundle === 'duo') {
          const v1 = findVariant(state.pair1);
          const v2 = findVariant(state.pair2);
          if (!v1 || !v1.available) { Toast.show('First pair is out of stock'); return; }
          if (!v2 || !v2.available) { Toast.show('Second pair is out of stock'); return; }
          const freeKitId = productData.freeKitVariantId;
          const items = [pairLine(v1, state.qty), pairLine(v2 || v1, state.qty)];
          if (freeKitId) items.push({ variantId: freeKitId, quantity: 1, properties: { _free_kit: 'true', _bundle: 'duo' } });
          await Cart.addItems(items);
        } else {
          const v1 = findVariant(state.pair1);
          if (!v1 || !v1.available) { Toast.show('This option is out of stock'); return; }
          await Cart.addItem(pairLine(v1, state.qty));
        }
        Toast.show('Added to bag');
        CartDrawer.open();
      } catch (err) {
        const msg = err.message || '';
        if (msg.toLowerCase().includes('all variants are sold out') || msg.toLowerCase().includes('cannot be added')) Toast.show('Sorry, this item is out of stock');
        else Toast.show(msg || 'Could not add to bag. Please try again.');
      } finally { state.addingToCart = false; setATCLoading(false); }
    }
    function setATCLoading(loading) {
      const atcBtns = [...sectionEl.querySelectorAll('[data-atc]'), ...(stickyAtc ? stickyAtc.querySelectorAll('[data-atc]') : [])];
      atcBtns.forEach(btn => { btn.disabled = loading; if (loading) { if (!btn.dataset.atcLabel) btn.dataset.atcLabel = btn.textContent.trim(); btn.innerHTML = '<span class="spinner"></span>'; } else { btn.textContent = btn.dataset.atcLabel || 'Add to cart'; } });
    }
    function initGallery() {
      const mainImg = sectionEl.querySelector('#gallery-main-img');
      const thumbs = [...sectionEl.querySelectorAll('.gallery-thumb')];
      const dots = [...sectionEl.querySelectorAll('.gallery-dot')];
      const prevBtn = sectionEl.querySelector('#gallery-prev');
      const nextBtn = sectionEl.querySelector('#gallery-next');
      let currentIdx = 0;
      function goTo(idx) {
        const count = thumbs.length;
        if (!count) return;
        currentIdx = ((idx % count) + count) % count;
        const thumb = thumbs[currentIdx];
        if (mainImg && thumb) {
          mainImg.style.opacity = '0.6';
          // Same fix as updateGalleryImage(): the static srcset rendered by
          // Liquid only describes the original featured image, so it must
          // be dropped or the browser keeps re-picking a stale srcset
          // candidate over the new src and the image never visually changes.
          mainImg.removeAttribute('srcset');
          mainImg.removeAttribute('sizes');
          mainImg.src = thumb.dataset.src || mainImg.src;
          mainImg.alt = thumb.dataset.alt || '';
          mainImg.onload = () => { mainImg.style.opacity = '1'; };
        }
        thumbs.forEach((t, i) => t.classList.toggle('is-active', i === currentIdx));
        dots.forEach((d, i) => d.classList.toggle('is-active', i === currentIdx));
      }
      thumbs.forEach((thumb, i) => { thumb.addEventListener('click', () => goTo(i)); });
      if (prevBtn) prevBtn.addEventListener('click', () => goTo(currentIdx - 1));
      if (nextBtn) nextBtn.addEventListener('click', () => goTo(currentIdx + 1));
      // Only the small 160px thumbnail is fetched up front — the full-res
      // image goTo() swaps in on swipe/tap was never downloaded, so the
      // very first swipe to each image waits on a network fetch and feels
      // laggy no matter how fast the handler itself runs. Prefetch every
      // full-res gallery image in the background right after init so by
      // the time a customer swipes, the image is already in cache.
      if ('requestIdleCallback' in window) {
        requestIdleCallback(() => { thumbs.forEach(t => { if (t.dataset.src) { const img = new Image(); img.src = t.dataset.src; } }); });
      } else {
        setTimeout(() => { thumbs.forEach(t => { if (t.dataset.src) { const img = new Image(); img.src = t.dataset.src; } }); }, 1000);
      }
      const galleryEl = sectionEl.querySelector('#gallery-main');
      if (galleryEl && thumbs.length > 1) {
        let touchStartX = 0, touchStartY = 0, isSwiping = false;
        galleryEl.addEventListener('touchstart', e => { touchStartX = e.touches[0].clientX; touchStartY = e.touches[0].clientY; isSwiping = false; }, { passive: true });
        galleryEl.addEventListener('touchmove', e => { const dx = e.touches[0].clientX - touchStartX; const dy = e.touches[0].clientY - touchStartY; if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 10) isSwiping = true; }, { passive: true });
        galleryEl.addEventListener('touchend', e => { if (!isSwiping) return; const dx = e.changedTouches[0].clientX - touchStartX; if (Math.abs(dx) > 40) goTo(dx < 0 ? currentIdx + 1 : currentIdx - 1); isSwiping = false; }, { passive: true });
      }
    }
    function initTabs() {
      $$('.tab-btn').forEach(btn => {
        btn.addEventListener('click', () => {
          const target = btn.dataset.tab;
          const tablist = btn.closest('[role="tablist"]');
          const container = tablist ? tablist.parentElement : document;
          $$(`.tab-btn`, container).forEach(b => { b.classList.remove('is-active'); b.setAttribute('aria-selected', 'false'); });
          $$(`.tab-panel`, container).forEach(p => p.classList.remove('is-active'));
          btn.classList.add('is-active');
          btn.setAttribute('aria-selected', 'true');
          const panel = container.querySelector('#tab-' + target);
          if (panel) panel.classList.add('is-active');
        });
      });
    }
    function startCountdown() {
      if (!countdownEl) return;
      const midnight = new Date();
      midnight.setHours(23, 59, 59, 0);
      const target = midnight.getTime();
      const tick = () => { const diff = Math.max(0, target - Date.now()); const h = Math.floor(diff / 3_600_000); const m = Math.floor((diff % 3_600_000) / 60_000); const s = Math.floor((diff % 60_000) / 1000); const pad = n => String(n).padStart(2, '0'); countdownEl.textContent = `${pad(h)}:${pad(m)}:${pad(s)}`; };
      tick();
      setInterval(tick, 1000);
    }
    function startBuyerRotation() {
      const el = sectionEl.querySelector('[data-buyer-notification]');
      if (!el) return;
      let order = BUYER_PEOPLE.map((_, i) => i);
      let cursor = 0;
      let lastIdx = -1;
      function shuffle() {
        for (let i = order.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1));[order[i], order[j]] = [order[j], order[i]]; }
        if (order[0] === lastIdx && order.length > 1) [order[0], order[1]] = [order[1], order[0]];
      }
      shuffle();
      const nameEl = el.querySelector('[data-buyer-name]');
      const cityEl = el.querySelector('[data-buyer-city]');
      const variantEl = el.querySelector('[data-buyer-variant]');
      const agoEl = el.querySelector('[data-buyer-ago]');
      const update = () => {
        if (cursor >= order.length) { shuffle(); cursor = 0; }
        const b = BUYER_PEOPLE[order[cursor]];
        lastIdx = order[cursor];
        cursor++;
        // Always a fresh "just happened" timestamp — never older than 60 minutes.
        const minsAgo = 1 + Math.floor(Math.random() * 45);
        if (nameEl) nameEl.textContent = b.name;
        if (cityEl) cityEl.textContent = b.city;
        if (variantEl) variantEl.textContent = randomVariantText();
        if (agoEl) agoEl.textContent = minsAgo === 1 ? '1 min ago' : `${minsAgo} min ago`;
      };
      update();
      setInterval(update, 8000);
    }
    function startViewerFluctuation() {
      const viewerPill = sectionEl.querySelector('[data-viewer-pill]');
      const countEl = viewerPill && viewerPill.querySelector('[data-viewer-count]');
      if (!countEl) return;
      const base = parseInt(countEl.textContent, 10) || 17;
      const min = Math.max(5, base - 8);
      const max = base + 12;
      let current = base;
      function flicker() {
        const delta = Math.floor(Math.random() * 3) + 1;
        const dir = Math.random() > 0.45 ? 1 : -1;
        current = Math.min(max, Math.max(min, current + delta * dir));
        // Only the number's own text node is touched — icon and surrounding
        // markup are never removed/reinserted, so the pill can never
        // momentarily collapse or change width unexpectedly.
        countEl.style.transition = 'opacity 0.3s';
        countEl.style.opacity = '0';
        setTimeout(() => { countEl.textContent = String(current); countEl.style.opacity = '1'; }, 300);
        const nextMs = (20 + Math.floor(Math.random() * 20)) * 1000;
        setTimeout(flicker, nextMs);
      }
      setTimeout(flicker, (20 + Math.floor(Math.random() * 20)) * 1000);
    }
    function startLowStockFluctuation() {
      const countEl = sectionEl.querySelector('[data-low-stock-count]');
      if (!countEl) return;
      const base = parseInt(countEl.textContent, 10) || 5;
      const min = Math.max(2, base - 2);
      const max = base + 3;
      let current = base;
      function flicker() {
        const dir = Math.random() > 0.6 ? 1 : -1;
        current = Math.min(max, Math.max(min, current + dir));
        countEl.style.transition = 'opacity 0.3s';
        countEl.style.opacity = '0';
        setTimeout(() => { countEl.textContent = String(current); countEl.style.opacity = '1'; }, 300);
        const nextMs = (45 + Math.floor(Math.random() * 45)) * 1000;
        setTimeout(flicker, nextMs);
      }
      setTimeout(flicker, (45 + Math.floor(Math.random() * 45)) * 1000);
    }
    function wire() {
      sectionEl.querySelectorAll('[data-bundle]').forEach(btn => {
        btn.addEventListener('click', () => {
          if (state.bundle === btn.dataset.bundle) {
            state.bundle = ''; state.pair1 = {}; state.pair2 = {}; state.upsellAcknowledged = false;
            updateBundleUI(); renderVariantPickers(); updateStickyAtc(); updateATCState(); updateATCPrice(); updateMainPrice();
          } else {
            selectBundle(btn.dataset.bundle);
          }
        });
      });
      wireBundleUpsell();
      if (consentBox) consentBox.addEventListener('change', () => { state.preorderAccepted = consentBox.checked; updateATCState(); });
      if (qtyDecEl) qtyDecEl.addEventListener('click', () => { state.qty = Math.max(1, state.qty - 1); if (qtyValEl) qtyValEl.textContent = state.qty; updateStickyAtc(); syncVariantInput(); updateATCPrice(); });
      if (qtyIncEl) qtyIncEl.addEventListener('click', () => { state.qty++; if (qtyValEl) qtyValEl.textContent = state.qty; updateStickyAtc(); syncVariantInput(); updateATCPrice(); });
      sectionEl.querySelectorAll('[data-atc]').forEach(btn => btn.addEventListener('click', handleATC));
      if (stickyAtc) stickyAtc.querySelectorAll('[data-atc]').forEach(btn => btn.addEventListener('click', handleATC));
      if (buyBox && stickyAtc) window.addEventListener('scroll', onScrollStickyAtc, { passive: true });
      sectionEl.querySelectorAll('[data-kit-swatch]').forEach(swatch => {
        swatch.addEventListener('click', () => {
          const idx = swatch.dataset.optIndex;
          sectionEl.querySelectorAll(`[data-kit-swatch][data-opt-index="${idx}"]`).forEach(s => { s.classList.remove('is-selected'); });
          swatch.classList.add('is-selected');
          const valEl = sectionEl.querySelector(`#kit-opt-val-${idx}`);
          if (valEl) valEl.textContent = swatch.dataset.optValue;
          if (variantIdInput) variantIdInput.value = swatch.dataset.variantId;
          updateATCState();
        });
      });
      initGallery(); initTabs(); startCountdown(); startBuyerRotation(); startViewerFluctuation(); startLowStockFluctuation();
      renderVariantPickers(); updateBundleUI(); updateStickyAtc(); updateATCState(); updateMainPrice();
    }
    wire();
    return { state, findVariant };
  }
  function initFBT() {
    $$('[data-fbt-add]').forEach(btn => {
      btn.addEventListener('click', async e => {
        e.preventDefault();
        const variantId = btn.dataset.variantId;
        if (!variantId) return;
        try { btn.textContent = '...'; await Cart.addItem({ variantId, quantity: 1 }); btn.textContent = 'Added'; setTimeout(() => { btn.textContent = 'Add'; }, 2000); CartDrawer.open(); }
        catch (err) { btn.textContent = 'Error'; Toast.show(err.message || 'Could not add'); }
      });
    });
  }
  function init() { $$('[data-product-section]').forEach(el => createInstance(el)); }
  return { init, initFBT };
})();

const ScrollReveal = (() => {
  function init() {
    if (!('IntersectionObserver' in window)) { $$('.reveal').forEach(el => el.classList.add('is-revealed')); return; }
    const observer = new IntersectionObserver((entries) => { entries.forEach(entry => { if (entry.isIntersecting) { entry.target.classList.add('is-revealed'); observer.unobserve(entry.target); } }); }, { rootMargin: '0px 0px -60px 0px', threshold: 0.08 });
    const selectors = ['.how-card', '.benefit-card', '.review-masonry-card', '.review-card', '.product-card', '.tiktok-card', '.faq-item', '.section-label', '.reviews-masonry-heading', '.tiktok-heading', '.featured-content h2', '.how-grid', '.benefits-grid', '.instagram-item', '.instagram-strip', '.bundle-option', '.before-after-section', '.press-bar', '.cta-section', '.fbt-card'];
    selectors.forEach(sel => { $$(sel).forEach(el => { el.classList.add('reveal'); observer.observe(el); }); });
    $$('.animate-fade-up').forEach(el => { if (!el.classList.contains('reveal')) { el.classList.add('reveal'); observer.observe(el); } });
  }
  return { init };
})();

// Shopify injects its native cookie-consent banner asynchronously via
// content_for_header, sometimes after this script runs — the CSS `display:
// none` rule alone can lose that race. Watch for it and hide it as soon as
// it appears, without touching the underlying consent-tracking logic.
(function hideCookieBanner() {
  const banner = document.getElementById('shopify-pc__banner');
  if (banner) { banner.style.display = 'none'; return; }
  const observer = new MutationObserver(() => {
    const b = document.getElementById('shopify-pc__banner');
    if (b) { b.style.display = 'none'; observer.disconnect(); }
  });
  observer.observe(document.documentElement, { childList: true, subtree: true });
})();

document.addEventListener('DOMContentLoaded', () => {
  Header.init();
  CartDrawer.init();
  EmailPopup.init();
  BeforeAfter.init();
  FAQ.init();
  ScrollReveal.init();
  ProductPage.init();
  ProductPage.initFBT();
  Cart.fetch();
  const shopBtn = document.getElementById('homepage-shop-btn');
  if (shopBtn) {
    const featuredSection = document.getElementById('featured-product');
    if (featuredSection) {
      let visible = false, ticking = false;
      function updateShopBtn() { ticking = false; const rect = featuredSection.getBoundingClientRect(); const shouldShow = rect.bottom < 0; if (shouldShow !== visible) { visible = shouldShow; shopBtn.classList.toggle('is-visible', shouldShow); } }
      window.addEventListener('scroll', () => { if (!ticking) { ticking = true; requestAnimationFrame(updateShopBtn); } }, { passive: true });
      updateShopBtn();
    }
  }
});

// ── Signup success + inline signup forms ─────────────────────────────────
// One success state for every signup surface: a green check that draws
// itself (CSS in theme.css, .al-check), "You're In!", and the address the
// personal code is going to.
function alEscape(s) {
  return String(s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}
function alSuccessHTML(email, note, withShopButton) {
  return '<div class="al-success" role="status">'
    + '<svg class="al-check" viewBox="0 0 52 52" aria-hidden="true" focusable="false"><circle class="al-check__circle" cx="26" cy="26" r="24"/><path class="al-check__mark" d="M15 27l7 7 15-15"/></svg>'
    + '<p class="al-success__title">You’re In!</p>'
    + (email ? '<p class="al-success__email">' + alEscape(email) + '</p>' : '')
    + '<p class="al-success__note">' + note + '</p>'
    + (withShopButton ? '<a class="btn-primary" href="/products/snapmags" style="width:100%;display:flex;align-items:center;justify-content:center;text-decoration:none;margin-top:1rem;">Shop SnapMags</a>' : '')
    + '<p class="al-success__hint">Not there in 5 minutes? Check spam or promotions.</p>'
    + '</div>';
}

// Homepage and footer signup forms (form[data-inline-signup]). They go through
// the same Klaviyo list as the popup, so they get the welcome flow and a
// personal code. Underneath each is a real Shopify customer form: if Klaviyo
// is unconfigured or refuses, the browser submits that instead, so the lead
// is never lost.
(function InlineSignup() {
  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  function markClaimed() {
    try { localStorage.setItem('allureis_popup_claimed', '1'); localStorage.setItem('allureis_popup_dismissed', '1'); } catch (_) {}
    document.cookie = 'allureis_popup_claimed=1; path=/; max-age=31536000; SameSite=Lax';
  }
  async function subscribe(cfg, email, source) {
    const payload = { data: { type: 'subscription', attributes: { custom_source: source, profile: { data: { type: 'profile', attributes: { email, subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } } } } } }, relationships: { list: { data: { type: 'list', id: cfg.listId } } } } };
    try {
      const res = await window.fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(cfg.publicKey), { method: 'POST', headers: { 'Content-Type': 'application/json', 'revision': '2026-07-15' }, body: JSON.stringify(payload) });
      return res.ok || res.status === 202;
    } catch (_) { return false; }
  }
  function bind(form) {
    form.addEventListener('submit', async (e) => {
      const cfg = window.__klaviyoConfig || {};
      if (!cfg.listId || !cfg.publicKey) return; // native Shopify submit
      e.preventDefault();
      const input = form.querySelector('input[type="email"]');
      const email = input ? input.value.trim() : '';
      if (!EMAIL_RE.test(email)) {
        if (input) { input.setCustomValidity('Enter a valid email address.'); input.reportValidity(); input.setCustomValidity(''); input.focus(); }
        return;
      }
      const btn = form.querySelector('[type="submit"]');
      if (btn) { btn.disabled = true; btn.textContent = 'Signing you up…'; }
      const ok = await subscribe(cfg, email, form.dataset.inlineSignup || 'Website Signup');
      if (!ok) { form.submit(); return; } // fall back to Shopify's own form
      markClaimed();
      const holder = document.createElement('div');
      holder.innerHTML = alSuccessHTML(email, 'Your personal 10% code is on its way to your inbox.', false);
      form.replaceWith(holder.firstChild);
    });
  }
  function init() { document.querySelectorAll('form[data-inline-signup]').forEach(bind); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
})();
